import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

import { ExternalSaleStatus } from '../../_mock/international-auctions.models';

/**
 * The four status labels, spelled out rather than concatenated at the call
 * site, so the keys stay greppable and survive a translation-extraction sweep.
 */
const STATUS_LABEL_KEYS: Record<ExternalSaleStatus, string> = {
  live: 'international_auctions.status.live',
  upcoming: 'international_auctions.status.upcoming',
  preview: 'international_auctions.status.preview',
  closed: 'international_auctions.status.closed',
};

/** Used when a `preview` sale reaches us without a house brand colour. */
const FALLBACK_BRAND = '#2E7D34';

/**
 * International Auctions — sale status pill.
 *
 * The mockup's `chip()` method, one-to-one:
 *
 * | status   | bg      | fg      | border  | dot     | motion  |
 * |----------|---------|---------|---------|---------|---------|
 * | live     | #EAF5EB | #1F5C25 | #BEDDC1 | #2E7D34 | pulsing |
 * | upcoming | #FFFFFF | #3C4A40 | #DCDCD4 | #9AA69C | none    |
 * | preview  | #FFFFFF | brand   | brand   | brand   | none    |
 * | closed   | #F4F4EE | #8B948C | #E4E4DC | #C3C9C3 | none    |
 *
 * Three of the four variants are pure CSS classes. Only `preview` needs data
 * from the component, because its colour is the auction house's brand colour;
 * that arrives as the `--ia-brand` custom property on the pill element.
 */
@Component({
  selector: 'app-international-status-chip',
  standalone: true,
  imports: [TranslatePipe],
  templateUrl: './international-status-chip.component.html',
  styleUrl: './international-status-chip.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InternationalStatusChipComponent {
  @Input({ required: true }) status!: ExternalSaleStatus;

  /** The house's `brand_color`. Only consulted for the `preview` variant. */
  @Input() brandColor: string | null = null;

  get labelKey(): string {
    return STATUS_LABEL_KEYS[this.status] || STATUS_LABEL_KEYS.upcoming;
  }

  /**
   * `null` for every variant but `preview`, which keeps the custom property
   * off the element entirely so the CSS fallbacks stay in charge.
   */
  get previewColor(): string | null {
    if (this.status !== 'preview') {
      return null;
    }
    const brand = (this.brandColor ?? '').trim();
    return brand.length > 0 ? brand : FALLBACK_BRAND;
  }
}
